/* ============
 * Products Index
 * ============
 *
 * This page shows the list of products.
 */

import { mapGetters, mapMutations } from 'vuex';
import productService from './../../../../services/product';
import accountService from './../../../../services/account';
import debounce from 'debounce';
import store from './../../../../store';
import SearchMixin from './../../../../mixins/search';
import productImportExportPopup from '../../../../components/productImportExportPopup/productImportExportPopup.vue';
import stringFormatMixin from './../../../../mixins/stringFormat';
import productTypeService from './../../../../services/productType';
import producerService from './../../../../services/producer';
import productIndexSettingsPopup from '../../../../components/productIndexSettingsPopup/productIndexSettingsPopup.vue';
import bulkProcessProductModal from '../../../../components/bulkProcessProductModal/bulkProcessProductModal.vue';

export default {
  data() {
    return {
      filterValue: [],
      loading: true,
      sort: '',
      info: 'info',
      primary: 'primary',
      search: '',
      limit: 25,
      multipleSelection: [],
      bulkSelectionLimit: 25,
      showAction: false,
    };
  },

  computed: {
    ...mapGetters({
      products: 'paginatedProducts',
      view: 'getView',
      grouping: 'productGrouping',
      searchQuery: 'productQuery',
      account: 'account',
      productTypes: 'allProductTypes',
      producers: 'allProducers',
    }),

    hasInventory() {
      if (this.account.modules.length > 0) {
        for (let i = 0; i < this.account.modules.length; i++) {
          if (this.account.modules[i].name == 'Inventory') {
            return true;
          }
        }
      }

      return false;
    },

    productGroupedCardView() {
      return this.products.items.reduce((groups, item) => {
        const group = (groups[(this.grouping === 'Producer' ? item.fieldsJSON.producer : item.productType) || 'none'] || []);
        group.push(item);
        groups[(this.grouping === 'Producer' ? item.fieldsJSON.producer : item.productType) || 'none'] = group;
        return groups;
      }, {});
    },
    productGroupedListView() {
      return this.products.items.reduce((groups, item) => {
        const group = (groups[(this.grouping === 'Producer' ? item.fieldsJSON.producer : item.productType) || 'none'] || []);
        group.push(item);
        groups[(this.grouping === 'Producer' ? item.fieldsJSON.producer : item.productType) || 'none'] = group.map((c) => {
          const product = this.products.items.find(p => p.id === c.id);
          const priceList = this.priceView(product.prices[0]);
          const data = {
            number: c.number,
            name: c.name,
            price: priceList,
            warehouses: this.displayWarehouse(product.warehouses),
            id: c.id,
          };
          return data;
        });
        return groups;
      }, {});
    },
    productsListView() {
      return this.products.items.map((c) => {
        const priceList = this.priceView(c.prices[0]);
        const data = { number: c.number,
          name: c.name,
          price: priceList,
          warehouses: this.displayWarehouse(c.warehouses),
          id: c.id,
        };
        return data;
      });
    },
    filters() {
      const filters = [{ value: 'type', label: this.$t('static.products.filters.type'), children: [] }, { value: 'producer', label: this.$t('static.products.filters.producer'), children: [] }];
      filters[0].children = this.productyTypeOptions.filter(item => item.value !== 'Bundle');
      filters[1].children = this.producers.map(item => ({ value: item.id, label: item.name }));
      return filters;
    },
    productyTypeOptions() {
      const options = this.productTypes.reduce((groups, item) => {
        const group = (groups[item.group] || {
          label: this.$t(`static.products.types.${this.camelize(item.group)}`),
          value: item.group,
          children: [],
        });
        if (item.group && item.subGroup) {
          const subGroup = (group.children[item.subGroup] || {
            label: this.$t(`static.products.types.${this.camelize(item.subGroup)}`),
            value: item.subGroup,
            children: [],
          });
          subGroup.children.push({
            label: this.$t(`static.products.types.${this.camelize(item.name)}`),
            value: item.name,
          });
          groups[item.group] = group;
          groups[item.group].children[item.subGroup] = subGroup;
        } else if (item.group) {
          group.children.push({
            label: this.$t(`static.products.types.${this.camelize(item.name)}`),
            value: item.name,
          });
          groups[item.group] = group;
        } else {
          groups[item.id] = {
            label: this.$t(`static.products.types.${this.camelize(item.name)}`),
            value: item.name,
          };
        }
        return groups;
      }, {});
      const opt = Object.keys(options).map((option) => {
        const item = options[option];
        if (item.children) {
          item.children = Object.keys(item.children).map((child) => {
            const c = item.children[child];
            if (c.children) {
              c.children = Object.keys(c.children).map(t => c.children[t]);
            }
            return c;
          });
        }
        return item;
      });
      return opt.sort((a, b) => ((a.label > b.label) ? 1 : -1));
    },

    selectedCount() {
      return this.multipleSelection.length || 0;
    },

    hasWooModule() {
      if (this.account.modules.length > 0) {
        if (this.account.modules.find(module => module.name === 'WordPress WooCommerce')) return true;
      }
      return false;
    },
  },

  components: {
    VCard: require('./../../../../components/card/card.vue'),
    VPagination: require('./../../../../components/paginationNew/paginationNew.vue'),
    VNoData: require('./../../../../components/noData/noData.vue'),
    VTable: require('./../../../../components/table/table.vue'),
  },

  watch: {
    /**
     * Listem to router changes, used to check query params that have changed to apply filters.
     *
     * @param  {Object} route Current route object.
     */
    $route() {
      this.multipleSelection = [];
      this.all(this.searchQuery.page, this.limit);
    },

    filterValue(filterValue) {
      const params = Object.assign({}, this.$route.query);
      let field = '';
      if (filterValue.length) {
        field = filterValue[0];
        const value = filterValue[filterValue.length - 1];
        params[field] = value;
      }
      if (field !== 'producer') {
        delete params.producer;
      }
      if (field !== 'type') {
        delete params.type;
      }
      this.$router.push({ query: params });
    },

    view(view) {
      this.showAction = false;
      if (view == 'card') {
        this.info = 'info';
        this.primary = 'primary';
      } else {
        this.primary = 'info';
        this.info = 'primary';
      }
    },
    grouping() {
      this.all(1, this.limit);
    },

    multipleSelection(multipleSelection) {
      this.showAction = false;
      if (multipleSelection.length) {
        this.showAction = true;
      }
    },
  },

  mounted() {
    let productFilter = {
      title: this.$t('static.products.filters.title'),
      options: [{
        title: this.$t('static.products.filters.withDeposit'),
        route: {
          name: 'products.index',
          query: {
            filter: 'withDeposit',
          },
        },
      },
      {
        title: this.$t('static.products.filters.active'),
        route: {
          name: 'products.index',
          query: {
            filter: 'active',
          },
        },
      },
      {
        title: this.$t('static.products.filters.deactivated'),
        route: {
          name: 'products.index',
          query: {
            filter: 'deactivated',
          },
        },
      },
      {
        title: this.$t('static.products.filters.materials'),
        route: {
          name: 'products.index',
          query: {
            type: 'Material',
          },
        },
      },
      {
        title: this.$t('static.products.filters.all'),
        route: {
          name: 'products.index',
        },
      },
      ],
      button: {
        title: `+ ${this.$t('static.products.create.title')}`,
        route: {
          name: 'products.create',
        },
      },
    };
    let vmpSyncMenu = {};
    if (this.account.modules.length > 0) {
      for (let i = 0; i < this.account.modules.length; i++) {
        if (this.account.modules[i].name == 'EDI') {
          productFilter = {
            title: this.$t('static.products.filters.title'),
            options: [{
              title: this.$t('static.products.filters.active'),
              route: {
                name: 'products.index',
                query: {
                  filter: 'active',
                },
              },
            },
            {
              title: this.$t('static.products.filters.deactivated'),
              route: {
                name: 'products.index',
                query: {
                  filter: 'deactivated',
                },
              },
            },
            {
              title: this.$t('static.products.filters.withDeposit'),
              route: {
                name: 'products.index',
                query: {
                  filter: 'withDeposit',
                },
              },
            },
            {
              title: this.$t('static.products.filters.materials'),
              route: {
                name: 'products.index',
                query: {
                  type: 'Material',
                },
              },
            },
            {
              title: this.$t('static.products.filters.asko'),
              route: {
                name: 'products.index',
                query: {
                  filter: 'asko',
                },
              },
            },
            {
              title: this.$t('static.products.filters.vinhuset'),
              route: {
                name: 'products.index',
                query: {
                  filter: 'vinhuset',
                },
              },
            },
            {
              title: this.$t('static.products.filters.marketplace'),
              route: {
                name: 'products.index',
                query: {
                  filter: 'marketplace',
                },
              },
            },
            {
              title: this.$t('static.products.filters.onlineStore'),
              route: {
                name: 'products.index',
                query: {
                  filter: 'onlineStore',
                },
              },
            },
            {
              title: this.$t('static.products.filters.alcohol'),
              route: {
                name: 'products.index',
                query: {
                  filter: 'alcohol',
                },
              },
            },
            {
              title: this.$t('static.products.filters.alcoholFree'),
              route: {
                name: 'products.index',
                query: {
                  filter: 'alcoholFree',
                },
              },
            },
            {
              title: this.$t('static.products.filters.all'),
              route: {
                name: 'products.index',
              },
            },
            ],
            button: {
              title: `+ ${this.$t('static.products.create.title')}`,
              route: {
                name: 'products.create',
              },
            },
          };
        }
        if (this.account.modules[i].name == 'Vinmonopolet') {
          vmpSyncMenu = {
            title: this.$t('static.products.syncVmp.title'),
            button: {
              title: `+ ${this.$t('static.products.syncVmp.title')}`,
              route: {
                name: 'products.syncVmp',
              },
            },
          };
        }
      }
    }

    const productBundle = {
      title: this.$t('static.productBundles.filters.title'),
      options: [{
        title: this.$t('static.productBundles.filters.all'),
        route: {
          name: 'productBundles.index',
        },
      }],
      button: {
        title: `+ ${this.$t('static.productBundles.create.title')}`,
        route: {
          name: 'productBundles.create',
        },
      },
    };

    this.setInnerSidebar([
      productFilter,
      productBundle,
      vmpSyncMenu,
      {
        title: this.$t('static.productPackagings.filters.title'),
        options: [{
          title: this.$t('static.productPackagings.filters.all'),
          route: {
            name: 'productPackagings.index',
          },
        }],
        button: {
          title: `+ ${this.$t('static.productPackagings.create.title')}`,
          route: {
            name: 'productPackagings.create',
          },
        },
      },
      {
        title: this.$t('static.productGroups.filters.title'),
        options: [{
          title: this.$t('static.productGroups.filters.all'),
          route: {
            name: 'productGroups.index',
          },
        }],
        button: {
          title: `+ ${this.$t('static.productGroups.create.title')}`,
          route: {
            name: 'productGroups.create',
          },
        },
      },
      {
        title: this.$t('static.productTags.filters.title'),
        options: [{
          title: this.$t('static.productTags.filters.all'),
          route: {
            name: 'productTags.index',
          },
        }],
        button: {
          title: `+ ${this.$t('static.productTags.create.title')}`,
          route: {
            name: 'productTags.create',
          },
        },
      },
      // {
      //   title: this.$t('static.exciseEntry.title'),
      //   options: [{
      //     title: this.$t('static.exciseEntry.filters.all'),
      //     route: {
      //       name: 'exciseentry.index',
      //     },
      //   }],
      // },
    ]);

    Promise.all([
      productTypeService.all(),
      producerService.all(),
    ]).then(() => {
      this.loading = false;
    });
    store.dispatch('setProductGrouping', this.account.productGrouping);

    accountService.rememberPerPage('get', { from: 'product' })
    .then((data) => {
      this.limit = data.size;
      this.all(this.searchQuery.page, this.limit);
    }).catch((error) => {
      console.log(error);
    });

    if (this.account.productDisplay) {
      store.dispatch('setView', this.account.productDisplay);
    }
    if (this.view == 'card') {
      this.info = 'info';
      this.primary = 'primary';
    } else {
      this.primary = 'info';
      this.info = 'primary';
    }
  },

  destroyed() {
    this.unsetInnerSidebar();
    store.dispatch('resetProducts');
  },

  methods: {
    ...mapMutations({
      setInnerSidebar: 'SET_INNERSIDEBAR',
      unsetInnerSidebar: 'UNSET_INNERSIDEBAR',
      toggleModal: 'TOGGLE_MODAL',
      setModalContent: 'SET_MODAL_CONTENT',
      setModalSize: 'SET_MODAL_SIZE',
      setModalProps: 'SET_MODAL_PROPS',
      unsetModalContent: 'UNSET_MODAL_CONTENT',
      setModalCloseOnClick: 'SET_MODAL_CLOSE_ON_CLICK',
      setModalShowClose: 'SET_MODAL_SHOW_CLOSE',
      setModalCloseOnPressEscape: 'SET_MODAL_CLOSE_ON_PRESS_ESCAPE',
      setModalClass: 'SET_MODAL_CLASS',
    }),

    onSelectAction(value) {
      if (this.multipleSelection.length > this.bulkSelectionLimit) {
        const message = `${this.$t('static.products.index.bulkMaxLimitExceededError')} ${this.bulkSelectionLimit}`;
        this.$message({
          showClose: true,
          message,
          customClass: 'error',
        });
        return;
      }

      if (value) {
        let type = 'Active';
        if (value === 'deactivateProduct') {
          type = 'Inactive';
        }

        this.setModalContent(() => bulkProcessProductModal);
        this.setModalProps({
          selectedProducts: this.multipleSelection,
          onSubmit: this.handleBulkProductActionSubmit,
          type: value,
        });
        this.setModalCloseOnClick(false);
        this.setModalShowClose(false);
        this.setModalCloseOnPressEscape(false);
        this.toggleModal(true);
      }
    },

    handleBulkProductActionSubmit() {
      this.multipleSelection = [];
      this.all(this.searchQuery.page, this.limit);
    },

    handleSelectionChange(val) {
      this.multipleSelection = val;
    },

    priceView(prices) {
      let priceList = '';
      for (const [key, value] of Object.entries(prices)) {
        const labelPrice = key !== 'price' ? `(${this.$t(`form.products.${key}.label`)})` : '';
        priceList += typeof value !== 'undefined' ? `NOK ${value.toFixed(2)} ${labelPrice}` : '';
        priceList += '<br />';
      }
      return priceList;
    },

    perPage(size) {
      accountService.rememberPerPage('post', { from: 'product', perPage: size })
      .then((data) => {
        this.limit = data.size;
      }).catch((error) => {
        console.log(error);
      });
    },

    handleFilterChange() {

    },
    showImportDownloadModal() {
      this.setModalContent(() => productImportExportPopup);
      this.setModalSize('full');
      this.toggleModal(true);
    },
    showProductIndexSettingsModal() {
      this.setModalClass('product-settings');
      this.setModalContent(() => productIndexSettingsPopup);
      this.setModalSize('full');
      this.toggleModal(true);
    },
    displayWarehouse(allWarehouses) {
      const map = {};
      const warehouses = allWarehouses.reduce((warehouse, val) => {
        const { warehouse: { id, name }, totalItemQty, totalCrateQty, totalLooseQty } = val;
        const { length: l } = warehouse;
        if (map.hasOwnProperty(id)) {
          warehouse[map[id]].totalItemQty += +totalItemQty;
          warehouse[map[id]].totalCrateQty += +totalCrateQty;
          warehouse[map[id]].totalLooseQty += +totalLooseQty;
        } else {
          map[id] = l;
          warehouse.push({
            id,
            name,
            totalItemQty: +totalItemQty,
            totalCrateQty: +totalCrateQty,
            totalLooseQty: +totalLooseQty,
          });
        }
        return warehouse;
      }, []);

      if (warehouses.length > 0) {
        let html = '';
        for (let i = 0; i < warehouses.length; i++) {
          if (warehouses[i].id) {
            const text = `${warehouses[i].name} (${warehouses[i].totalItemQty} : ${warehouses[i].totalCrateQty} kasse(r), ${warehouses[i].totalLooseQty} løse )`;
            html += `<a title="${text}" href="#/inventory/warehouse/${warehouses[i].id}/edit">${text}</a><br>`;
          }
        }
        return html;
      }
      return '';
    },

   // Warehouse X ([total_items] : 100 case(s), 6 items)

    changeView(view) {
      if (this.info != view) {
        this.primary = this.info;
        this.info = view;
        if (this.info == 'info') {
          store.dispatch('setView', 'card');
        } else {
          store.dispatch('setView', 'list');
        }
      }
    },
    /**
     * Fetches paginated products
     *
     * @param  {int} page The page number
     */
    all: debounce(function (page, limit) {
      this.loading = true;

      const query = { page, limit, search: this.searchQuery.search, grouping: this.searchQuery.grouping };
      store.dispatch('setProductQuery', query);
      this.search = this.searchQuery.search;
      productService.items({
        page,
        limit,
        search: this.searchQuery.search,
        grouping: this.searchQuery.grouping,
        filter: this.$route.query.filter,
        type: this.$route.query.type,
        producer: this.$route.query.producer,
        packaging: JSON.stringify(this.$route.query.packaging),
      })
        .then(() => {
          this.loading = false;
        }).catch(() => {
          this.loading = false;
        });
    }, 800),

    onChangeSearch(name, value) {
      store.dispatch('setProductQuery', { page: 1, limit: this.limit, search: value, grouping: this.searchQuery.grouping });
      this.all(1, this.limit);
    },
    /**
     * Go to edit view for product group
     *
     * @parObject} productGroup The product group object
     */
    editProduct(product) {
      this.$router.push({
        name: 'products.edit',
        params: { id: product.id },
      });
    },

    /**
     * Go to edit view for product in new tab
     *
     * @parObject} productGroup The product object
     */
    editProductNewTab(product) {
      const route = this.$router.resolve({
        name: 'products.edit',
        params: { id: product.id },
      });
      window.open(route.href, '_blank');
    },
  },

  mixins: [
    SearchMixin,
    stringFormatMixin,
  ],
};



// WEBPACK FOOTER //
// ./src/app/pages/withNavigation/products/index/index.js